import type { ReactNode } from 'react';
import { cn } from '@rocco/utils/cn';
import { BLEED_GUTTER_X } from '~/ui-rocco/pages/detail-shared';
import { ArticleTile } from '@rocco/ui/tile-article/components/ArticleTile';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';

/**
 * `category` is the /articles/{category} grid (Figma 49:4495) and `directory`
 * the /articles landing grid (Figma 60:2076). Both lock their image to the same
 * 437x524 so every card in a row shows the same picture height; they differ in
 * their column width, their gutter and their row gap.
 */
type EditorialGridLayout = 'category' | 'directory';

interface EditorialArticleGridProps {
    articles: ArticleTileViewModel[];
    layout?: EditorialGridLayout;
    /** Rendered over each card's media, e.g. the film play affordance. */
    renderOverlay?: (article: ArticleTileViewModel) => ReactNode;
    className?: string;
}

const GRID_LAYOUT: Record<EditorialGridLayout, string> = {
    // 48px margins, four 432px columns, 32px gutter, 79px rows.
    category: `gap-x-[2rem] gap-y-[4.9375rem] px-[1.5rem] ${BLEED_GUTTER_X}`,
    // 48px margins, four 437px columns, 24px gutter, 100px rows.
    directory: `gap-x-[1.5rem] gap-y-[6.25rem] px-[1.5rem] ${BLEED_GUTTER_X}`,
};

/**
 * The four-up editorial article grid.
 *
 * `1rem` is 16px at 1920, so the Figma pixel values carry straight over as rem
 * and scale with the viewport below that.
 */
export const EditorialArticleGrid = ({
    articles,
    layout = 'category',
    renderOverlay,
    className,
}: EditorialArticleGridProps) => {
    if (articles.length === 0) return null;

    return (
        <div
            className={cn(
                'grid grid-cols-2 md:grid-cols-4',
                GRID_LAYOUT[layout],
                className,
            )}
        >
            {articles.map((article) => (
                <div key={article.id}>
                    <ArticleTile
                        model={article}
                        aspectRatio="featured"
                        rounded={false}
                        overlay={renderOverlay?.(article)}
                    />
                </div>
            ))}
        </div>
    );
};
